/**
 * SecuritySection Component
 *
 * Security and LGPD compliance summary to build trust.
 */

import { library } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faShieldHalved, faLock, faFileShield, faUserShield, faClipboardCheck, faDatabase } from '@fortawesome/free-solid-svg-icons';

library.add(faShieldHalved, faLock, faFileShield, faUserShield, faClipboardCheck, faDatabase);

import { FadeIn } from './FadeIn';

interface SecurityItem {
  icon: typeof faShieldHalved;
  title: string;
  description: string;
}

const SECURITY_ITEMS: SecurityItem[] = [
  {
    icon: faUserShield,
    title: 'Controle de Acesso',
    description:
      'Seus dados só são acessíveis por profissionais com quem você estabeleceu uma relação explícita. Você decide quem vê.',
  },
  {
    icon: faLock,
    title: 'Criptografia',
    description:
      'Todos os dados são criptografados em trânsito e em repouso, seguindo os mais altos padrões de segurança para dados de saúde.',
  },
  {
    icon: faClipboardCheck,
    title: 'Conformidade LGPD',
    description:
      'Plataforma desenvolvida em conformidade com a Lei Geral de Proteção de Dados, com base legal, transparência e direitos do titular.',
  },
  {
    icon: faFileShield,
    title: 'Trilha de Auditoria',
    description:
      'Registro completo de quem acessou seus dados, quando e por quê. Transparência total sobre o uso das suas informações.',
  },
  {
    icon: faDatabase,
    title: 'Residência de Dados',
    description:
      'Seus dados de saúde permanecem armazenados em território brasileiro, em conformidade com as exigências regulatórias do setor.',
  },
];

function SecurityCard({ icon, title, description }: SecurityItem) {
  return (
    <div className="flex items-start gap-4 p-4 rounded-xl border bg-card/50 hover:border-primary/20 transition-colors">
      <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center text-primary shrink-0">
        <FontAwesomeIcon icon={icon} />
      </div>
      <div>
        <h3 className="font-semibold text-sm mb-1">{title}</h3>
        <p className="text-xs text-muted-foreground leading-relaxed">{description}</p>
      </div>
    </div>
  );
}

export function SecuritySection() {
  return (
    <section className="w-full max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24 lg:py-32">
      <FadeIn>
        <div className="mx-auto flex max-w-[58rem] flex-col items-center justify-center gap-4 text-center mb-12">
          <div className="rounded-2xl bg-green-500/10 px-4 py-1.5 text-sm font-medium text-green-600 dark:text-green-400">
            Segurança e Privacidade
          </div>
          <h2 className="font-heading text-3xl font-bold leading-[1.1] sm:text-4xl md:text-5xl">
            Seus dados de saúde <span className="text-primary">protegidos</span>
          </h2>
          <p className="max-w-[85%] leading-normal text-muted-foreground sm:text-lg sm:leading-7">
            A segurança e privacidade dos seus dados são a base da plataforma Examfy. Conheça nossas práticas de proteção.
          </p>
        </div>
      </FadeIn>

      <FadeIn className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {SECURITY_ITEMS.map((item, index) => (
          <SecurityCard key={index} {...item} />
        ))}
      </FadeIn>
    </section>
  );
}
